import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/adminAPI';
import Navbar from '../components/Navbar';
import './AdminDashboard.css';

function AdminDashboard() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUserData, setNewUserData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    subscriptionPlan: 'free',
    subscriptionStatus: 'active',
    stripeCustomerId: '',
    stripeSubscriptionId: '',
    isAdmin: false,
  });

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.getAllUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (
      !newUserData.email ||
      !newUserData.password ||
      !newUserData.firstName ||
      !newUserData.lastName
    ) {
      alert('Please fill in all required fields');
      return;
    }

    if (newUserData.password.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    setCreating(true);
    try {
      await adminAPI.createUser(newUserData);
      alert('User created successfully!');
      setShowCreateModal(false);
      setNewUserData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        subscriptionPlan: 'free',
        subscriptionStatus: 'active',
        stripeCustomerId: '',
        stripeSubscriptionId: '',
        isAdmin: false,
      });
      fetchUsers(); // Refresh list
    } catch (error) {
      console.error('Create user error:', error);
      alert(error.response?.data?.error || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleNewUserChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewUserData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const pendingDeletionUsers = users.filter((u) => u.deletedAt);

  const filteredUsers = users.filter(
    (user) =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'free':
        return '#718096';
      case 'pro':
        return '#667eea';
      case 'premium':
        return '#f59e0b';
      default:
        return '#718096';
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="admin-container">
          <div className="loading-state">Loading admin dashboard...</div>
        </div>
      </>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <Navbar />
      <div className="admin-container">
        <div className="admin-content">
          <button onClick={() => navigate('/admin')} className="back-button">
            ← Back to Admin Home
          </button>
          <div className="users-admin-header">
            <div>
              <h1>Admin Portal</h1>
              <p>Manage users and subscriptions</p>
            </div>
            <button
              className="btn btn-primary"
              style={{ width: 'auto', flexShrink: 0, marginBottom: 10 }}
              onClick={() => setShowCreateModal(true)}
            >
              + Create New User
            </button>
          </div>

          {/* Search Bar */}
          <div className="search-bar">
            <input
              type="text"
              placeholder="🔍 Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* ── Business Overview ── */}
          <div className="admin-section-label">Business Overview</div>
          <div className="stats-grid stats-grid-wide">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-info">
                <div className="stat-value">{stats?.totalUsers ?? users.length}</div>
                <div className="stat-label">Total Users</div>
              </div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid #22c55e' }}>
              <div className="stat-icon">📅</div>
              <div className="stat-info">
                <div className="stat-value" style={{ color: '#22c55e' }}>{stats?.newUsersThisWeek ?? '—'}</div>
                <div className="stat-label">New This Week</div>
              </div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid #3b82f6' }}>
              <div className="stat-icon">📆</div>
              <div className="stat-info">
                <div className="stat-value" style={{ color: '#3b82f6' }}>{stats?.newUsersThisMonth ?? '—'}</div>
                <div className="stat-label">New This Month</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⭐</div>
              <div className="stat-info">
                <div className="stat-value">{users.filter((u) => u.subscriptionPlan === 'pro').length}</div>
                <div className="stat-label">Pro Users</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💎</div>
              <div className="stat-info">
                <div className="stat-value">{users.filter((u) => u.subscriptionPlan === 'premium').length}</div>
                <div className="stat-label">Premium Users</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🆓</div>
              <div className="stat-info">
                <div className="stat-value">{users.filter((u) => u.subscriptionPlan === 'free').length}</div>
                <div className="stat-label">Free Users</div>
              </div>
            </div>
          </div>

          {/* ── Product Usage ── */}
          <div className="admin-section-label">Product Usage</div>
          <div className="stats-grid stats-grid-wide">
            <div className="stat-card">
              <div className="stat-icon">⚾</div>
              <div className="stat-info">
                <div className="stat-value">{stats?.totalPlayers ?? '—'}</div>
                <div className="stat-label">Players Created</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-info">
                <div className="stat-value">{stats?.totalGames ?? '—'}</div>
                <div className="stat-label">Games Tracked</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🏆</div>
              <div className="stat-info">
                <div className="stat-value">{stats?.totalBragCards ?? '—'}</div>
                <div className="stat-label">BragCards Created</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🎨</div>
              <div className="stat-info">
                <div className="stat-value">{stats?.totalGraphics ?? '—'}</div>
                <div className="stat-label">Graphics Created</div>
              </div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid #f59e0b' }}>
              <div className="stat-icon">💡</div>
              <div className="stat-info">
                <div className="stat-value" style={{ color: '#f59e0b' }}>{stats?.totalAnalystMessages ?? '—'}</div>
                <div className="stat-label">Analyst Messages (Mo)</div>
              </div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid #c0001a' }}>
              <div className="stat-icon">⚠️</div>
              <div className="stat-info">
                <div className="stat-value" style={{ color: '#c0001a' }}>{pendingDeletionUsers.length}</div>
                <div className="stat-label">Pending Deletion</div>
              </div>
            </div>
          </div>

          {/* ── Quick Links ── */}
          <div className="admin-section-label">Quick Links</div>
          <div className="stats-grid" style={{ marginBottom: 32 }}>
            <div className="stat-card" onClick={() => navigate('/admin/seasons')} style={{ cursor: 'pointer' }}>
              <div className="stat-icon">🗓️</div>
              <div className="stat-info">
                <div className="stat-label">Manage Seasons</div>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Role</th>
                  <th>Players</th>
                  <th>Games</th>
                  <th>BragCards</th>
                  <th>Analyst</th>
                  <th>Joined</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty-message">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="user-cell">
                          <div className="user-avatar">
                            {user.firstName.charAt(0)}
                            {user.lastName.charAt(0)}
                          </div>
                          <div className="user-info">
                            <div className="user-name">
                              {user.firstName} {user.lastName}
                              {user.isAdmin && (
                                <span className="admin-badge">👑 Admin</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {user.email}
                        {user.deletedAt && (
                          <span style={{
                            marginLeft: 8, background: '#c0001a', color: '#fff',
                            fontSize: 10, fontWeight: 700, padding: '2px 6px',
                            borderRadius: 4, verticalAlign: 'middle', letterSpacing: 0.5,
                          }}>PENDING DELETION</span>
                        )}
                      </td>
                      <td>
                        <span
                          className="plan-badge"
                          style={{
                            background: getPlanBadgeColor(
                              user.subscriptionPlan
                            ),
                          }}
                        >
                          {user.subscriptionPlan.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ textTransform: 'capitalize' }}>{user.role || '—'}</td>
                      <td>{user.playerCount || 0}</td>
                      <td>{user.gameCount || 0}</td>
                      <td>{user.bragCardCount || 0}</td>
                      <td>{user.aiCoachUsageCount || 0}</td>
                      <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                      <td>{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : '—'}</td>
                      <td>
                        <button
                          className="btn-view"
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                          style={{ position: 'relative' }}
                        >
                          View Details →
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowCreateModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New User</h2>
              <button
                className="modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateUser}>
              <div className="modal-body">
                {/* Basic Info */}
                <div className="form-section">
                  <h3>Basic Information</h3>

                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={newUserData.email}
                      onChange={handleNewUserChange}
                      required
                      placeholder="user@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label>Password *</label>
                    <input
                      type="password"
                      name="password"
                      value={newUserData.password}
                      onChange={handleNewUserChange}
                      required
                      minLength="6"
                      placeholder="Minimum 6 characters"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={newUserData.firstName}
                        onChange={handleNewUserChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={newUserData.lastName}
                        onChange={handleNewUserChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        name="isAdmin"
                        checked={newUserData.isAdmin}
                        onChange={handleNewUserChange}
                      />{' '}
                      Grant Admin Access
                    </label>
                  </div>
                </div>

                {/* Subscription */}
                <div className="form-section">
                  <h3>Subscription</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Plan</label>
                      <select
                        name="subscriptionPlan"
                        value={newUserData.subscriptionPlan}
                        onChange={handleNewUserChange}
                      >
                        <option value="free">Free</option>
                        <option value="pro">Pro</option>
                        <option value="premium">Premium</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Status</label>
                      <select
                        name="subscriptionStatus"
                        value={newUserData.subscriptionStatus}
                        onChange={handleNewUserChange}
                      >
                        <option value="active">Active</option>
                        <option value="canceled">Canceled</option>
                        <option value="past_due">Past Due</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Stripe (Optional) */}
                <div className="form-section">
                  <h3>Stripe Integration (Optional)</h3>

                  <div className="form-group">
                    <label>Stripe Customer ID</label>
                    <input
                      type="text"
                      name="stripeCustomerId"
                      value={newUserData.stripeCustomerId}
                      onChange={handleNewUserChange}
                      placeholder="cus_..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Stripe Subscription ID</label>
                    <input
                      type="text"
                      name="stripeSubscriptionId"
                      value={newUserData.stripeSubscriptionId}
                      onChange={handleNewUserChange}
                      placeholder="sub_..."
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creating}
                >
                  {creating ? 'Creating...' : '✓ Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
